import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import ClientProfileHeader from './components/ClientProfileHeader';
import MinuteManagement from './components/MinuteManagement';
import SessionManagement from './components/SessionManagement';
import TransactionHistory from './components/TransactionHistory';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { clientApiService } from '../../utils/clientApiService';
import { transactionApiService } from '../../utils/transactionApiService';

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [activeManagementTab, setActiveManagementTab] = useState('minutes');

  useEffect(() => {
    const fetchClient = async () => {
      setIsLoading(true);
      try {
        const fetchedClient = await clientApiService.getClientById(clientId);
        setClient(fetchedClient);
        const txs = await transactionApiService.getTransactions(clientId);
        setTransactions(txs);
      } catch (error) {
        setClient(null);
        setTransactions([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchClient();
  }, [clientId]);

  const getCurrentUsername = () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return 'Unknown';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  const handleSessionTransaction = async (type, amount, description) => {
    if (!client) return;

    try {
      const currentSessions = client.sessions || 0;
      const newSessions = type === 'add' ? currentSessions + amount : currentSessions - amount;

      if (newSessions < 0) {
        throw new Error('Insufficient sessions - transaction would result in negative sessions');
      }

      // Record session transaction in history (keep amount for minutes, use "sessions" field for sessions)
      await transactionApiService.addTransaction({
        clientId: client.id,
        type,
        sessions: amount,
        description,
        staff: getCurrentUsername()
      });

      // Update client sessions using the same client endpoint, sending "sessions" instead of "balance"
      await clientApiService.updateClient(client.id, {
        sessions: newSessions
      });

      // Refresh client and transaction history
      const updatedClient = await clientApiService.getClientById(client.id);
      setClient(updatedClient);
      const txs = await transactionApiService.getTransactions(client.id);
      setTransactions(txs);

      return { success: true };
    } catch (error) {
      console.error('Session transaction error:', error);
      return { success: false, error: error.message };
    }
  };

  const handleMinuteTransaction = async (type, amount, description) => {
    if (!client) return;
    try {
      // Call backend to add transaction and update balance
      await transactionApiService.addTransaction({
        clientId: client.id,
        type,
        amount,
        description,
        staff: getCurrentUsername()
      });
      // Refresh client and transaction history
      const updatedClient = await clientApiService.getClientById(client.id);
      setClient(updatedClient);
      const txs = await transactionApiService.getTransactions(client.id);
      setTransactions(txs);
      return { success: true };
    } catch (error) {
      console.error('Transaction error:', error);
      return { success: false, error: error.message };
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <main className="pt-16">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="text-muted-foreground">Loading client profile...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <main className="pt-16">
          <div className="container mx-auto px-6 py-8">
            <div className="text-center">
              <Icon name="UserX" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Client Not Found</h1>
              <p className="text-muted-foreground mb-6">
                The client profile you're looking for doesn't exist or has been removed.
              </p>
              <Button onClick={handleGoBack} variant="outline">
                Go Back
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <main className="pt-16">
        {/* Breadcrumb Navigation */}
        <div className="bg-muted/20 border-b border-border">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center space-x-2 text-sm">
              <button
                onClick={() => navigate('/client-dashboard')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </button>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              <button
                onClick={handleGoBack}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Search Results
              </button>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              <span className="text-foreground font-medium">{client.name}</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Profile & Balance */}
            <div className="lg:col-span-2 space-y-6">
              <ClientProfileHeader client={client} />

              <div className="space-y-4">
                <div className="flex rounded-lg bg-muted p-1">
                  <button
                    type="button"
                    onClick={() => setActiveManagementTab('minutes')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                      activeManagementTab === 'minutes'
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Clock" size={16} />
                    <span>Minute Management</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveManagementTab('sessions')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                      activeManagementTab === 'sessions'
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Users" size={16} />
                    <span>Session Management</span>
                  </button>
                </div>

                {activeManagementTab === 'minutes' ? (
                  <MinuteManagement
                    client={client}
                    onTransaction={handleMinuteTransaction}
                  />
                ) : (
                  <SessionManagement
                    client={client}
                    onSessionTransaction={handleSessionTransaction}
                  />
                )}
              </div>
            </div>

            {/* Right Column - Transaction History */}
            <div className="lg:col-span-1">
              <TransactionHistory transactions={transactions} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientDetail;